import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { PartnerListItem, PartnerStatus, PartnerToggleOptions } from '@models/partners';
import { ColDef } from 'ag-grid-community';
import { CuiGuardLoadingIndicatorComponent, CuiTableModule, formatDate } from 'cui-components';
import { PartnersStore, PartnersStoreType } from 'src/app/app-state/partners/partners.store';

export const PARTNERS_TABLE_ID = 'partners-table';
@Component({
  imports: [CuiTableModule, RouterModule, CuiGuardLoadingIndicatorComponent],
  selector: 'hui-partners',
  template: `
    <cui-table
      [tableId]="PARTNERS_TABLE_ID"
      id="partners-table"
      [columnDefs]="columnDefs"
      [rowData]="partners()"
      [toggleDefaultOption]="toggleOption()"
      [toggleOptions]="ToggleOptions"
      [isLoading]="isLoading()"
      [selectedRowId]="selectedPartnerId()"
      [navigationFn]="navigateToPartner"
      (toggleClicked)="onToggleClick($event)"
    ></cui-table>

    <router-outlet></router-outlet>
    <cui-guard-loading-indicator [urlToCheck]="'/partners'"></cui-guard-loading-indicator>
  `,
  styleUrls: ['./partners.component.scss'],
})
export class PartnersComponent {
  private partnersStore: PartnersStoreType = inject(PartnersStore);

  partners = this.partnersStore.partners;
  isLoading = this.partnersStore.isLoading;
  selectedPartnerId = this.partnersStore.selectedPartnerId;
  toggleOption = this.partnersStore.toggle;

  ToggleOptions: PartnerToggleOptions[] = [PartnerToggleOptions.Operative, PartnerToggleOptions.All];
  PARTNERS_TABLE_ID = PARTNERS_TABLE_ID;
  columnDefs: ColDef<PartnerListItem>[] = [
    { field: 'companyName', headerName: 'Name', minWidth: 300, maxWidth: 700 },
    { field: 'mpnId', headerName: 'MPN ID', minWidth: 150 },
    {
      field: 'country',
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        applyMiniFilterWhileTyping: true,
      },
    },
    {
      field: 'region',
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        applyMiniFilterWhileTyping: true,
      },
    },
    {
      field: 'membershipLevel',
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        applyMiniFilterWhileTyping: true,
      },
    },
    {
      field: 'lastUpdate',
      headerName: 'Last update date time',
      valueFormatter: ({ value }) => formatDate(new Date(value), 'yyyy-MM-dd HH:mm'),
    },
    { field: 'domainName' },
    {
      field: 'status',
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        applyMiniFilterWhileTyping: true,
      },
      valueFormatter: ({ value }: { value: PartnerStatus }) => (value === 'MarkedForDeletion' ? 'Marked for deletion' : value),
      cellRenderer: 'cuiTableCellColoredChipRenderer',
    },
    {
      headerName: 'Activated for Cloud Hub',
      field: 'applicationActivated',
      cellRenderer: 'cuiTableCellCheckmarkRenderer',
      valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
    },
    { field: 'id', hide: true },
    { field: 'crmAccountId', hide: true },
    { field: 'entraTenantId', hide: true },
    { field: 'expectedTenantId', hide: true },
  ];

  constructor() {
    this.partnersStore.loadPartners();
  }

  navigateToPartner = (partner: PartnerListItem): Promise<boolean> => this.partnersStore.goToPartnerActions(partner.id);

  onToggleClick(toggle: PartnerToggleOptions): void {
    this.partnersStore.loadPartners(toggle);
  }
}
